import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { parseServerDate } from '../utils/formatDate';

const PLAN_HIGHLIGHTS = {
  free:       ['1 company workspace', '50-question assessment', '2 dimensions in report', '2 insights shown', '10 AI messages/month', 'Basic radar chart'],
  pro:        ['3 company workspaces', '120-question 12-dimension audit', 'Full diagnostic report', 'All insights + heatmap', '200 AI messages/month', 'PDF + PPTX export', 'Document intelligence', 'Multi-round comparison', '30-60-90 day roadmap', 'AI narrative summary'],
  enterprise: ['Unlimited companies', 'Everything in Pro', 'Unlimited AI messages', 'White-label reports', 'Priority support', 'Custom dimensions', 'API access (Phase 6)', 'SSO / SAML (Phase 6)'],
};

export default function Billing({ showToast }) {
  const { authFetch, user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [upgrading, setUpgrading] = useState('');

  useEffect(() => {
    authFetch('/api/billing/plans').then(r => r.json()).then(d => setPlans(Array.isArray(d) ? d : []));
    authFetch('/api/billing/transactions').then(r => r.json()).then(d => setTxns(Array.isArray(d) ? d : []));
  }, [authFetch]);

  const upgrade = async (planId) => {
    setUpgrading(planId);
    try {
      const res = await authFetch('/api/billing/create-order', { method: 'POST', body: JSON.stringify({ plan_id: planId }) });
      const data = await res.json();

      // ── Server error ──────────────────────────────────────────────────────
      if (!res.ok) {
        showToast(data.error || 'Order creation failed. Check server logs.', 'error');
        setUpgrading('');
        return;
      }

      // ── Demo mode (no Razorpay keys configured) ───────────────────────────
      if (data.demo_mode) {
        const demoRes  = await authFetch('/api/billing/demo-upgrade', { method: 'POST', body: JSON.stringify({ plan_id: planId }) });
        const demoData = await demoRes.json();
        if (demoRes.ok) {
          showToast(`✅ ${demoData.message}`);
          refreshUser?.();
          authFetch('/api/billing/transactions').then(r => r.json()).then(d => setTxns(Array.isArray(d) ? d : []));
        } else {
          showToast(demoData.error || 'Demo upgrade failed', 'error');
        }
        setUpgrading('');
        return;
      }

      // ── Real Razorpay checkout ────────────────────────────────────────────
      if (!data.id) {
        showToast(data.error || 'Order ID missing — check server configuration', 'error');
        setUpgrading('');
        return;
      }

      // Load Razorpay script only if not already loaded
      const loadRzp = () => new Promise((resolve) => {
        if (window.Razorpay) { resolve(); return; }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = resolve;
        document.head.appendChild(script);
      });

      await loadRzp();

      const rzp = new window.Razorpay({
        key:          data.key_id,
        order_id:     data.id,
        amount:       data.amount,
        currency:     data.currency || 'INR',
        name:         'Enpower Org DNA™',
        description:  plans.find(p => p.id === planId)?.name || planId,
        prefill:      data.prefill || {},
        theme:        { color: 'var(--gold)' },
        handler: async (response) => {
          const verifyRes = await authFetch('/api/billing/verify-payment', {
            method: 'POST',
            body: JSON.stringify({ ...response, plan_id: planId })
          });
          const verifyData = await verifyRes.json();
          if (verifyRes.ok) {
            showToast('Payment successful! Plan upgraded.');
            refreshUser?.();
            authFetch('/api/billing/transactions').then(r => r.json()).then(d => setTxns(Array.isArray(d) ? d : []));
          } else {
            showToast(verifyData.error || 'Payment verification failed. Contact support.', 'error');
          }
          setUpgrading('');
        },
        modal: { ondismiss: () => setUpgrading('') }
      });
      rzp.open();

    } catch (e) {
      showToast(e.message || 'Request failed. Is the server running?', 'error');
      setUpgrading('');
    }
  };

  const isPro = user?.plan === 'pro' || user?.plan === 'enterprise';

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Billing & Plans</div>
        <div className="page-sub">Upgrade for full diagnostic power — all 12 dimensions, AI, PDF/PPTX export</div>
      </div>

      {/* Current plan */}
      <div className="card" style={{ marginBottom: 24, background: 'linear-gradient(135deg,rgba(0,212,255,0.06),var(--purple-soft))', borderColor: 'var(--blue-border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4, fontFamily: 'var(--ff-m)', textTransform: 'uppercase', letterSpacing: 1 }}>Current Plan</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className={`badge plan-${user?.plan}`} style={{ fontSize: 14, padding: '5px 14px' }}>● {user?.plan?.toUpperCase()}</span>
              <span style={{ fontFamily: 'var(--ff-m)', fontSize: 14, color: 'var(--gold)' }}>⚡ {user?.credits} AI credits</span>
            </div>
          </div>
          {!isPro && (
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>You're on the free plan. Upgrade to unlock the full platform.</div>
          )}
          {isPro && (
            <div style={{ fontSize: 13, color: 'var(--green)' }}>✓ Full access active</div>
          )}
        </div>
        {PLAN_HIGHLIGHTS[user?.plan] && (
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PLAN_HIGHLIGHTS[user.plan].map(f => (
              <span key={f} className="tag" style={{ fontSize: 11 }}>✓ {f}</span>
            ))}
          </div>
        )}
      </div>

      {/* Plans */}
      <div className="sec-header"><div className="sec-title">Available Plans</div><div className="sec-line"></div></div>
      <div className="g3" style={{ marginBottom: 32 }}>
        {[
          { id: 'free', name: 'Free', price: '₹0', sub: 'Forever', color: 'var(--text3)', border: 'var(--border)', features: PLAN_HIGHLIGHTS.free, badge: null },
          { id: 'pro_monthly', name: 'Pro', price: '₹6,999', sub: '/month', color: 'var(--gold-2)', border: 'rgba(0,212,255,0.4)', features: PLAN_HIGHLIGHTS.pro, badge: 'MOST POPULAR', cta: 'Upgrade to Pro' },
          { id: 'pro_annual', name: 'Pro Annual', price: '₹69,990', sub: '/year (save ₹14K)', color: 'var(--accent2)', border: 'rgba(167,139,250,0.4)', features: PLAN_HIGHLIGHTS.pro, badge: 'BEST VALUE', cta: 'Upgrade Annual' },
        ].map(plan => (
          <div key={plan.id} className="card" style={{ borderColor: plan.border, position: 'relative', paddingTop: plan.badge ? 36 : 20 }}>
            {plan.badge && (
              <div style={{ position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)', background: plan.color, color: '#000', fontSize: 9, fontWeight: 700, padding: '3px 12px', borderRadius: '0 0 8px 8px', letterSpacing: 1, fontFamily: 'var(--ff-m)', whiteSpace: 'nowrap' }}>{plan.badge}</div>
            )}
            <div style={{ fontFamily: 'var(--ff-m)', fontSize: 11, color: plan.color, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>{plan.name}</div>
            <div style={{ fontFamily: 'var(--ff-m)', fontSize: 30, fontWeight: 700, color: plan.color, lineHeight: 1 }}>{plan.price}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18, marginTop: 3 }}>{plan.sub}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
              {plan.features.map(f => (
                <div key={f} style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <span style={{ color: plan.color, flexShrink: 0 }}>✓</span>{f}
                </div>
              ))}
            </div>
            {plan.cta && (
              <button className={`btn btn-primary`} style={{ width: '100%', justifyContent: 'center', borderColor: plan.border, color: plan.color, background: `${plan.color}18` }}
                disabled={upgrading === plan.id}
                onClick={() => upgrade(plan.id)}>
                {upgrading === plan.id ? 'Processing...' : plan.cta}
              </button>
            )}
            {plan.id === 'free' && (
              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', marginTop: 8 }}>
                {user?.plan === 'free' ? '● Current Plan' : 'Downgrade not supported'}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Credit packs */}
      <div className="sec-header"><div className="sec-title">AI Credit Packs</div><div className="sec-line"></div><span className="tag">Works on any plan</span></div>
      <div className="g2" style={{ marginBottom: 32 }}>
        {[
          { id: 'credits_50',  label: '50 Credits',  price: '₹999',   sub: '₹20/credit',  color: 'var(--gold-2)' },
          { id: 'credits_150', label: '150 Credits', price: '₹2,499', sub: '₹16.66/credit', color: 'var(--green)' },
        ].map(pack => (
          <div key={pack.id} className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontFamily: 'var(--ff-m)', fontSize: 20, fontWeight: 600, color: pack.color }}>{pack.label}</div>
              <div style={{ fontFamily: 'var(--ff-m)', fontSize: 14, color: 'var(--text)', margin: '2px 0' }}>{pack.price}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{pack.sub}</div>
            </div>
            <button className="btn btn-primary btn-sm" style={{ borderColor: `${pack.color}60`, color: pack.color }} disabled={upgrading === pack.id} onClick={() => upgrade(pack.id)}>
              {upgrading === pack.id ? 'Processing...' : 'Buy Credits'}
            </button>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <div className="sec-header"><div className="sec-title">Transaction History</div><div className="sec-line"></div></div>
      {txns.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '28px', color: 'var(--text3)', fontSize: 13 }}>No transactions yet</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table">
            <thead><tr><th>Description</th><th>Amount</th><th>Credits</th><th>Status</th><th>Date</th><th>Invoice</th></tr></thead>
            <tbody>
              {txns.map(t => (
                <tr key={t.id}>
                  <td style={{ fontSize: 13 }}>{t.description || t.plan}</td>
                  <td style={{ fontFamily: 'var(--ff-m)', fontSize: 12, color: 'var(--green)' }}>{t.amount > 0 ? `₹${(t.amount/100).toLocaleString('en-IN')}` : 'Free'}</td>
                  <td style={{ fontFamily: 'var(--ff-m)', fontSize: 12, color: 'var(--gold)' }}>{t.credits_added > 0 ? `+${t.credits_added}` : '—'}</td>
                  <td><span className={`tag ${t.status === 'completed' ? 'priority-strong' : 'priority-weak'}`} style={{ fontSize: 10 }}>{t.status}</span></td>
                  <td style={{ fontSize: 11, color: 'var(--text3)' }}>{parseServerDate(t.created_at).toLocaleDateString('en-IN')}</td>
                  <td>
                    {t.status === 'completed' ? (
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '3px 8px' }}
                        onClick={() => {
                          const token = localStorage.getItem('token');
                          fetch(`/api/invoices/generate/${t.id}`, { headers: { Authorization: `Bearer ${token}` } })
                            .then(r => r.text()).then(html => {
                              const w = window.open('', '_blank');
                              w.document.write(html); w.document.close();
                            });
                        }}>
                        🧾 GST Invoice
                      </button>
                    ) : <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card" style={{ marginTop: 24, padding: '12px 16px', borderColor: 'var(--blue-soft)', background: 'rgba(0,212,255,0.02)' }}>
        <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
          <strong style={{ color: 'var(--gold-2)' }}>Demo mode active</strong> (no Razorpay keys configured) — upgrades apply instantly for testing.
          Add <code style={{ fontFamily: 'var(--ff-m)', fontSize: 11, color: 'var(--text)' }}>RAZORPAY_KEY_ID</code> and <code style={{ fontFamily: 'var(--ff-m)', fontSize: 11, color: 'var(--text)' }}>RAZORPAY_KEY_SECRET</code> to <code style={{ fontFamily: 'var(--ff-m)', fontSize: 11, color: 'var(--text)' }}>server/.env</code> to enable real payments.
        </div>
      </div>
    </div>
  );
}
